import React, { useRef, useEffect, useState, useCallback } from "react";

const MATRIX_CHARS = "アイウエオカキクケコ0123456789ABCDEF<>[]{}|~!@#$%^&*";

function MatrixCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const cols = Math.floor(window.innerWidth / 16);
    const drops = Array(cols).fill(0).map(() => Math.random() * -100);
    function draw() {
      ctx.fillStyle = "rgba(0,0,0,0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#00ff41";
      ctx.font = "13px monospace";
      drops.forEach((y, i) => {
        const char = MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
        ctx.fillText(char, i * 16, y * 16);
        if (y * 16 > canvas.height && Math.random() > 0.975) drops[i] = 0;
        else drops[i] += 1;
      });
      animId = requestAnimationFrame(draw);
    }
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return (
    <canvas ref={canvasRef} style={{
      position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
      zIndex: 0, opacity: 0.12, pointerEvents: "none"
    }} />
  );
}

function KpiCard({ label, value, color, sub }) {
  return (
    <div style={SK.kpiCard}>
      <div style={{ ...SK.kpiValue, color }}>{value}</div>
      <div style={SK.kpiLabel}>{label}</div>
      {sub && <div style={{ ...SK.kpiSub, color }}>{sub}</div>}
    </div>
  );
}

function KpiBar() {
  const [mrr, setMrr] = useState(null);
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState(null);
  const [buffet, setBuffet] = useState(null);
  const [n8nOk, setN8nOk] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const r = await fetch("/api/agents/status");
      const d = await r.json();
      if (d.success) setAgents(d.agents || []);
    } catch (_) {}
    try {
      const r = await fetch("/api/leads/count");
      const d = await r.json();
      setLeads(d.count ?? d.total ?? "?");
    } catch (_) {}
    try {
      const r = await fetch("/api/buffet/query");
      const d = await r.json();
      setBuffet(d.active ?? d.total ?? (Array.isArray(d.apis) ? d.apis.length : null));
    } catch (_) {}
    try {
      const r = await fetch("/api/mrr");
      const d = await r.json();
      if (d.mrr !== undefined) setMrr(d.mrr);
    } catch (_) {}
    try {
      const r = await fetch("/api/n8n/status");
      const d = await r.json();
      setN8nOk(d.active ?? d.running ?? null);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 20000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const activeAgents = agents.filter(a => a.status === "active").length;
  const totalAgents = agents.length || 17;
  return (
    <div style={SK.kpiBar}>
      <KpiCard label="MRR" value={mrr !== null ? "€" + Number(mrr).toLocaleString("fr-FR") : "—"} color="#00ff41" />
      <KpiCard label="AGENTS" value={activeAgents + "/" + totalAgents} color={activeAgents === totalAgents ? "#00ff41" : "#ffaa00"} sub={activeAgents === totalAgents ? "ALL ACTIVE" : (totalAgents - activeAgents) + " DOWN"} />
      <KpiCard label="LEADS" value={leads !== null ? String(leads) : "—"} color="#00ccff" />
      <KpiCard label="BUFFET APIs" value={buffet !== null ? String(buffet) : "—"} color="#aa88ff" />
      <KpiCard label="N8N" value={n8nOk !== null ? n8nOk + " ACTIFS" : "—"} color={n8nOk && n8nOk > 0 ? "#00ff41" : "#cc0000"} />
    </div>
  );
}

function AgentsGrid() {
  const [agents, setAgents] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const fetchAgents = useCallback(async () => {
    try {
      const r = await fetch("/api/agents/status");
      const d = await r.json();
      if (d.success) { setAgents(d.agents || []); setLastUpdate(new Date().toLocaleTimeString("fr-FR")); }
    } catch (_) {}
  }, []);
  useEffect(() => { fetchAgents(); const t = setInterval(fetchAgents, 15000); return () => clearInterval(t); }, [fetchAgents]);
  return (
    <div style={SK.agentsCard}>
      <div style={SK.cardTitle}>
        ◉ AGENTS v2.6.0
        <span style={SK.agentCount}>{agents.filter(a => a.status === "active").length}/{agents.length || 17}</span>
        {lastUpdate && <span style={SK.lastUpdate}>MAJ {lastUpdate}</span>}
      </div>
      <div style={SK.agentsGrid}>
        {agents.length === 0 && <div style={SK.dimText}>Chargement agents...</div>}
        {agents.map(a => (
          <div key={a.name} style={{ ...SK.agentChip, borderColor: a.status === "active" ? "rgba(0,255,65,0.3)" : "rgba(204,0,0,0.3)", background: a.status === "active" ? "rgba(0,255,65,0.04)" : "rgba(204,0,0,0.04)" }}>
            <span style={{ ...SK.agentDot, background: a.status === "active" ? "#00ff41" : "#cc0000", boxShadow: a.status === "active" ? "0 0 5px rgba(0,255,65,0.7)" : "0 0 5px rgba(204,0,0,0.7)" }} />
            <div style={SK.agentInfo}>
              <div style={SK.agentName}>{a.name}</div>
              <div style={SK.agentRole}>{a.role}</div>
            </div>
            <div style={SK.agentPort}>{a.port}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FounderCockpit() {
  const [token, setToken] = useState(() => sessionStorage.getItem("_fcjwt") || "");
  const [loginPass, setLoginPass] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [containers, setContainers] = useState([]);
  const [restartingMap, setRestartingMap] = useState({});
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [evaAnomaly, setEvaAnomaly] = useState(false);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const containerTimerRef = useRef(null);
  const audioCtxRef = useRef(null);

  useEffect(() => {
    const checkEva = async () => {
      try {
        const r = await fetch("/api/eva/status");
        if (!r.ok) { setEvaAnomaly(true); return; }
        const d = await r.json();
        setEvaAnomaly(d.status !== "ok" && d.status !== "OK");
      } catch { setEvaAnomaly(true); }
    };
    checkEva();
    const t = setInterval(checkEva, 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    document.title = "COCKPIT FONDATEUR — SELENA MATRIX";
    return () => { document.title = "SELENA MATRIX | DNCM Solution"; };
  }, []);

  const scrollBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(scrollBottom, [messages]);

  const getAudioCtx = () => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed")
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  };

  const fetchContainers = useCallback(async (jwt) => {
    const t = jwt || token;
    if (!t) return;
    try {
      const r = await fetch("/api/founder/containers", { headers: { "Authorization": "Bearer " + t } });
      if (r.status === 401) { sessionStorage.removeItem("_fcjwt"); setToken(""); return; }
      const d = await r.json();
      setContainers(d.containers || []);
    } catch (_) {}
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchContainers(token);
    containerTimerRef.current = setInterval(() => fetchContainers(token), 10000);
    return () => clearInterval(containerTimerRef.current);
  }, [token, fetchContainers]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginErr("");
    try {
      const r = await fetch("/api/founder/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: loginPass }) });
      const d = await r.json();
      if (!r.ok) { setLoginErr(d.error || "Erreur d'authentification"); return; }
      sessionStorage.setItem("_fcjwt", d.token);
      setToken(d.token);
    } catch (_) { setLoginErr("Connexion impossible"); }
    finally { setLoginLoading(false); }
  };

  const restartContainer = async (name) => {
    setRestartingMap(m => ({ ...m, [name]: true }));
    try { await fetch("/api/founder/restart/" + name, { method: "POST", headers: { "Authorization": "Bearer " + token } }); } catch (_) {}
    setTimeout(() => { setRestartingMap(m => ({ ...m, [name]: false })); fetchContainers(token); }, 4000);
  };

  const speakText = useCallback(async (text) => {
    if (!text) return;
    setSpeaking(true);
    try {
      const r = await fetch("/api/voice/tts", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: text.slice(0, 500) }) });
      if (!r.ok) return;
      const buf = await r.arrayBuffer();
      const ctx = getAudioCtx();
      if (ctx.state === "suspended") await ctx.resume();
      const decoded = await ctx.decodeAudioData(buf);
      const src = ctx.createBufferSource();
      src.buffer = decoded;
      src.connect(ctx.destination);
      src.start(0);
      src.onended = () => setSpeaking(false);
    } catch (_) { setSpeaking(false); }
  }, []);

  const sendMessage = useCallback(async (message) => {
    if (!message.trim() || streaming) return;
    const historySnapshot = messages.map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: "user", content: message }]);
    setInputMsg("");
    setStreaming(true);
    let assistantContent = "";
    setMessages(prev => [...prev, { role: "assistant", content: "" }]);
    try {
      const r = await fetch("/api/chat/stream", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message, persona: "selena-dncm", history: historySnapshot }) });
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines2 = buf.split("\n");
        buf = lines2.pop();
        for (const line of lines2) {
          if (!line.startsWith("data:")) continue;
          const d = line.slice(5).trim();
          if (d === "[DONE]") break;
          try { const parsed = JSON.parse(d); if (parsed.token) { assistantContent += parsed.token; setMessages(prev => { const next = [...prev]; next[next.length - 1] = { role: "assistant", content: assistantContent }; return next; }); } } catch (_) {}
        }
      }
      if (assistantContent) speakText(assistantContent);
    } catch (e) { setMessages(prev => { const next = [...prev]; next[next.length - 1] = { role: "assistant", content: "[Erreur: " + e.message + "]" }; return next; }); }
    finally { setStreaming(false); }
  }, [messages, streaming, speakText]);

  const toggleRecording = async () => {
    if (recording) { mediaRecorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/ogg";
      const mr = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        try {
          const r = await fetch("/api/stt", { method: "POST", headers: { "Authorization": "Bearer " + token, "Content-Type": mimeType }, body: blob });
          const d = await r.json();
          if (d.text && d.text.trim()) sendMessage(d.text.trim());
        } catch (_) {}
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) { alert("Microphone inaccessible: " + e.message); }
  };

  const logout = () => { sessionStorage.removeItem("_fcjwt"); setToken(""); setMessages([]); setContainers([]); };

  if (!token) {
    return (
      <div style={S.page}>
        <MatrixCanvas />
        <div style={S.loginBox}>
          <div style={S.loginLogo}>◈</div>
          <div style={S.loginTitle}>COCKPIT FONDATEUR</div>
          <div style={S.loginSub}>SELENA MATRIX — ACCÈS RESTREINT</div>
          <form onSubmit={doLogin} style={S.loginForm} autoComplete="off">
            <input type="text" name="username" style={{ display: "none" }} readOnly />
            <input type="password" name="founder-pass" value={loginPass} onChange={e => setLoginPass(e.target.value)} placeholder="MOT DE PASSE FONDATEUR" style={S.loginInput} autoComplete="new-password" autoFocus />
            {loginErr && <div style={S.loginErr}>{loginErr}</div>}
            <button type="submit" disabled={loginLoading} style={S.btnPrimary}>{loginLoading ? "CONNEXION EN COURS..." : "▶  ACCÉDER AU COCKPIT"}</button>
          </form>
          <div style={S.loginFooter}>DNCM SOLUTION — SYSTÈME SÉCURISÉ</div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <MatrixCanvas />
      <div style={S.cockpit}>
        <div style={S.header}>
          <div style={S.headerLeft}><span style={S.headerDot} /><span style={S.headerTitle}>COCKPIT FONDATEUR — SELENA MATRIX v2.6.0</span></div>
          <div style={S.headerRight}>
            {speaking && <span style={S.speakingBadge}>◉ PAROLE</span>}
            {streaming && <span style={S.streamingBadge}>▶ STREAMING</span>}
            {evaAnomaly && (
              <span style={{ background: "#cc0000", color: "#fff", fontFamily: "monospace", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4, letterSpacing: 1, boxShadow: "0 0 10px rgba(204,0,0,0.7)" }}>
                ⚠ EVA ANOMALIE
              </span>
            )}
            <button
              type="button"
              onClick={() => { window.location.href = "/cockpit/carte-magique"; }}
              style={{
                background: "rgba(137,66,190,0.12)",
                border: "1px solid rgba(205,150,255,0.45)",
                color: "#d9a9ff",
                padding: "4px 12px",
                fontFamily: "inherit",
                fontSize: 10,
                cursor: "pointer",
                letterSpacing: 1.5
              }}
            >
              ◆ CARTE MAGIQUE
            </button>
            <button onClick={logout} style={S.logoutBtn}>DÉCONNECTER</button>
          </div>
        </div>
        <KpiBar />
        <div style={S.body}>
          <div style={S.leftPanel}>
            <div style={S.card}>
              <div style={S.cardTitle}>◉ INTERFACE VOCALE SELENA</div>
              <div style={S.avatarZone}>
                <img src="/assets/selena_avatar.png" alt="SELENA" style={S.avatarImg} />
                <div style={S.avatarOverlay}><span style={S.avatarLabel}>SELENA</span><span style={S.avatarSub}>AI MATRIX</span></div>
              </div>
              <div style={S.voiceRow}>
                <button onClick={toggleRecording} style={recording ? S.btnRecording : S.btnMic}>{recording ? "⬛  ARRÊTER" : "⬤  MICRO"}</button>
              </div>
              {recording && <div style={S.recordingPulse}>● ENREGISTREMENT EN COURS...</div>}
            </div>
            <AgentsGrid />
            <div style={{ ...S.card, flex: 1, minHeight: 0 }}>
              <div style={S.cardTitle}>◉ CONTAINERS DOCKER <span style={SK.agentCount}>{containers.length}</span></div>
              <div style={S.containerList}>
                {containers.length === 0 && <div style={S.dimText}>Chargement containers...</div>}
                {containers.map(c => (
                  <div key={c.id} style={S.containerRow}>
                    <span style={{ ...S.statusDot, background: c.state === "running" ? "#00ff41" : "#cc0000", boxShadow: c.state === "running" ? "0 0 6px rgba(0,255,65,0.6)" : "0 0 6px rgba(204,0,0,0.6)" }} />
                    <div style={S.containerInfo}><div style={S.containerName}>{c.name}</div><div style={S.containerStatus}>{c.status}</div></div>
                    <button onClick={() => restartContainer(c.name)} disabled={!!restartingMap[c.name]} style={S.restartBtn} title={"Redémarrer " + c.name}>{restartingMap[c.name] ? "..." : "↺"}</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={S.rightPanel}>
            <div style={{ ...S.card, flex: 1, minHeight: 0 }}>
              <div style={S.cardTitle}>◉ COMMUNICATION OPÉRATIONNELLE — SELENA</div>
              <div style={S.chatMessages}>
                {messages.length === 0 && (<div style={S.chatWelcome}><div style={S.welcomeIcon}>◈</div><div style={S.welcomeTitle}>SELENA MATRIX OPÉRATIONNELLE</div><div style={S.welcomeSub}>Posez votre question ou activez le microphone</div></div>)}
                {messages.map((m, i) => (
                  <div key={i} style={m.role === "user" ? S.msgWrapUser : S.msgWrapAssistant}>
                    <div style={m.role === "user" ? S.msgLabelUser : S.msgLabelAssistant}>{m.role === "user" ? "FONDATEUR" : "SELENA"}</div>
                    <div style={m.role === "user" ? S.msgBubbleUser : S.msgBubbleAssistant}>{m.content || (streaming && i === messages.length - 1 ? "▌" : "")}</div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div style={S.chatInputRow}>
                <input value={inputMsg} onChange={e => setInputMsg(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(inputMsg); } }} placeholder="Message à SELENA..." style={S.chatInput} disabled={streaming} />
                <button onClick={() => sendMessage(inputMsg)} disabled={streaming || !inputMsg.trim()} style={S.btnSend}>▶</button>
                <button onClick={toggleRecording} style={recording ? S.btnMicSmallRec : S.btnMicSmall} title="Micro STT">⬤</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const SK = {
  kpiBar: { display: "flex", gap: 8, padding: "8px 16px", borderBottom: "1px solid rgba(0,255,65,0.08)", background: "rgba(0,8,0,0.95)", flexShrink: 0, flexWrap: "wrap" },
  kpiCard: { flex: "1 1 140px", minWidth: 120, background: "rgba(0,15,0,0.8)", border: "1px solid rgba(0,255,65,0.1)", borderRadius: 2, padding: "8px 14px", display: "flex", flexDirection: "column", gap: 2 },
  kpiValue: { fontSize: 18, fontWeight: "bold", letterSpacing: 1, lineHeight: 1.2 },
  kpiLabel: { fontSize: 9, color: "#335533", letterSpacing: 3 },
  kpiSub: { fontSize: 9, letterSpacing: 2, opacity: 0.7 },
  agentsCard: { background: "rgba(0,15,0,0.7)", border: "1px solid rgba(0,255,65,0.1)", borderRadius: 2, padding: 14, display: "flex", flexDirection: "column", gap: 8 },
  cardTitle: { fontSize: 10, letterSpacing: 3, color: "#00ff41", fontWeight: "bold", display: "flex", alignItems: "center", gap: 8 },
  agentCount: { background: "rgba(0,255,65,0.1)", border: "1px solid rgba(0,255,65,0.2)", padding: "1px 6px", borderRadius: 2, fontSize: 10, color: "#00cc33" },
  lastUpdate: { fontSize: 8, color: "#334433", letterSpacing: 1, marginLeft: "auto" },
  agentsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, maxHeight: 340, overflowY: "auto" },
  agentChip: { display: "flex", alignItems: "center", gap: 6, padding: "5px 7px", border: "1px solid", borderRadius: 2 },
  agentDot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
  agentInfo: { flex: 1, minWidth: 0 },
  agentName: { fontSize: 9, color: "#00cc44", fontWeight: "bold", letterSpacing: 1 },
  agentRole: { fontSize: 8, color: "#334433", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  agentPort: { fontSize: 8, color: "#334433", flexShrink: 0 },
  dimText: { color: "#222", fontSize: 11, padding: 8 }
};

const S = {
  page: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "#050505", fontFamily: "'JetBrains Mono', 'Courier New', monospace", color: "#00ff41", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" },
  loginBox: { position: "relative", zIndex: 10, background: "rgba(0,5,0,0.95)", border: "1px solid rgba(0,255,65,0.4)", padding: "40px 48px", borderRadius: 2, minWidth: 380, textAlign: "center", boxShadow: "0 0 60px rgba(0,255,65,0.1)" },
  loginLogo: { fontSize: 32, marginBottom: 16, opacity: 0.8 },
  loginTitle: { fontSize: 18, fontWeight: "bold", letterSpacing: 5, marginBottom: 6, color: "#00ff41" },
  loginSub: { fontSize: 10, color: "#444", marginBottom: 32, letterSpacing: 3 },
  loginForm: { display: "flex", flexDirection: "column", gap: 12 },
  loginInput: { background: "rgba(0,20,0,0.8)", border: "1px solid rgba(0,255,65,0.3)", color: "#00ff41", padding: "12px 16px", fontFamily: "inherit", fontSize: 13, letterSpacing: 2, outline: "none", width: "100%", boxSizing: "border-box" },
  loginErr: { color: "#cc0000", fontSize: 11, letterSpacing: 1 },
  btnPrimary: { background: "rgba(0,255,65,0.9)", color: "#050505", border: "none", padding: "14px 24px", fontFamily: "inherit", fontSize: 12, fontWeight: "bold", letterSpacing: 3, cursor: "pointer", width: "100%" },
  loginFooter: { marginTop: 24, fontSize: 9, color: "#222", letterSpacing: 3 },
  cockpit: { position: "relative", zIndex: 10, display: "flex", flexDirection: "column", width: "100%", height: "100vh", background: "rgba(0,0,0,0.88)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", borderBottom: "1px solid rgba(0,255,65,0.12)", background: "rgba(0,10,0,0.9)", flexShrink: 0 },
  headerLeft: { display: "flex", alignItems: "center", gap: 10 },
  headerDot: { width: 8, height: 8, borderRadius: "50%", background: "#00ff41", boxShadow: "0 0 8px #00ff41" },
  headerTitle: { fontSize: 12, letterSpacing: 4, fontWeight: "bold", color: "#00ff41" },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  speakingBadge: { fontSize: 10, color: "#00ff41", letterSpacing: 2, opacity: 0.7 },
  streamingBadge: { fontSize: 10, color: "#ffaa00", letterSpacing: 2 },
  logoutBtn: { background: "transparent", border: "1px solid rgba(204,0,0,0.5)", color: "#cc0000", padding: "4px 12px", fontFamily: "inherit", fontSize: 10, cursor: "pointer", letterSpacing: 2 },
  body: { display: "flex", flex: 1, overflow: "hidden" },
  leftPanel: { width: 340, display: "flex", flexDirection: "column", gap: 10, padding: 12, borderRight: "1px solid rgba(0,255,65,0.08)", overflowY: "auto", flexShrink: 0 },
  rightPanel: { flex: 1, display: "flex", flexDirection: "column", padding: 12, overflow: "hidden" },
  card: { background: "rgba(0,15,0,0.7)", border: "1px solid rgba(0,255,65,0.1)", borderRadius: 2, padding: 16, display: "flex", flexDirection: "column" },
  cardTitle: { fontSize: 10, letterSpacing: 3, color: "#00ff41", marginBottom: 14, fontWeight: "bold", display: "flex", alignItems: "center", gap: 8 },
  avatarZone: { position: "relative", height: 200, overflow: "hidden", border: "1px solid rgba(0,255,65,0.2)", borderRadius: 2, background: "#000" },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" },
  avatarOverlay: { position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 12px", background: "linear-gradient(transparent, rgba(0,0,0,0.82))", display: "flex", flexDirection: "column" },
  avatarLabel: { fontSize: 12, letterSpacing: 4, fontWeight: "bold", color: "#00ff41" },
  avatarSub: { fontSize: 8, letterSpacing: 3, color: "#336633", marginTop: 2 },
  voiceRow: { display: "flex", gap: 8, marginTop: 12 },
  btnMic: { background: "transparent", border: "1px solid rgba(0,255,65,0.4)", color: "#00ff41", padding: "9px 16px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", letterSpacing: 2, flex: 1 },
  btnRecording: { background: "rgba(204,0,0,0.15)", border: "1px solid rgba(204,0,0,0.6)", color: "#cc0000", padding: "9px 16px", fontFamily: "inherit", fontSize: 11, cursor: "pointer", letterSpacing: 2, flex: 1 },
  recordingPulse: { color: "#cc0000", fontSize: 10, marginTop: 8, letterSpacing: 2, textAlign: "center", opacity: 0.8 },
  containerList: { display: "flex", flexDirection: "column", gap: 5, overflowY: "auto", flex: 1 },
  containerRow: { display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: "rgba(0,0,0,0.35)", border: "1px solid rgba(0,255,65,0.05)", borderRadius: 2 },
  statusDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  containerInfo: { flex: 1, minWidth: 0 },
  containerName: { fontSize: 11, color: "#00cc44", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  containerStatus: { fontSize: 9, color: "#335533", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 },
  restartBtn: { background: "transparent", border: "1px solid rgba(0,255,65,0.15)", color: "#446644", width: 26, height: 26, cursor: "pointer", fontFamily: "inherit", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0, borderRadius: 2 },
  dimText: { color: "#222", fontSize: 11, padding: 8 },
  chatMessages: { flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, padding: "4px 2px", minHeight: 0 },
  chatWelcome: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 8, padding: "60px 20px" },
  welcomeIcon: { fontSize: 28, opacity: 0.4 },
  welcomeTitle: { fontSize: 13, letterSpacing: 3, fontWeight: "bold" },
  welcomeSub: { fontSize: 11, color: "#446644", letterSpacing: 1 },
  msgWrapUser: { display: "flex", flexDirection: "column", alignItems: "flex-end" },
  msgWrapAssistant: { display: "flex", flexDirection: "column", alignItems: "flex-start" },
  msgLabelUser: { fontSize: 9, letterSpacing: 2, color: "#446644", marginBottom: 4 },
  msgLabelAssistant: { fontSize: 9, letterSpacing: 2, color: "#00ff41", marginBottom: 4 },
  msgBubbleUser: { background: "rgba(0,40,0,0.5)", border: "1px solid rgba(0,255,65,0.15)", padding: "10px 14px", borderRadius: "2px 0 2px 2px", fontSize: 13, lineHeight: 1.6, color: "#99ffaa", maxWidth: "80%", whiteSpace: "pre-wrap", wordBreak: "break-word" },
  msgBubbleAssistant: { background: "rgba(0,20,0,0.7)", border: "1px solid rgba(0,255,65,0.08)", padding: "10px 14px", borderRadius: "0 2px 2px 2px", fontSize: 13, lineHeight: 1.6, color: "#ccffcc", maxWidth: "85%", whiteSpace: "pre-wrap", wordBreak: "break-word" },
  chatInputRow: { display: "flex", gap: 8, marginTop: 12, flexShrink: 0 },
  chatInput: { flex: 1, background: "rgba(0,10,0,0.8)", border: "1px solid rgba(0,255,65,0.2)", color: "#00ff41", padding: "11px 14px", fontFamily: "inherit", fontSize: 13, outline: "none", borderRadius: 2 },
  btnSend: { background: "rgba(0,255,65,0.85)", color: "#050505", border: "none", padding: "11px 22px", fontFamily: "inherit", fontSize: 14, fontWeight: "bold", cursor: "pointer", borderRadius: 2 },
  btnMicSmall: { background: "transparent", border: "1px solid rgba(0,255,65,0.3)", color: "#00ff41", padding: "11px 14px", fontFamily: "inherit", cursor: "pointer", borderRadius: 2 },
  btnMicSmallRec: { background: "rgba(204,0,0,0.15)", border: "1px solid rgba(204,0,0,0.5)", color: "#cc0000", padding: "11px 14px", fontFamily: "inherit", cursor: "pointer", borderRadius: 2 }
};
